import { CNS_API_BASE } from "@/config";
import {
  CNSLearning,
  CNSLearningPage,
  LearningStatus,
  JsonPatchOperation,
} from "@/models/cns";
import { throwOnProblemResponse } from "@/types/problem-detail";

// Fetch learnings for a project
export async function fetchLearnings(projectId: string): Promise<CNSLearning[]> {
  const res = await fetch(`${CNS_API_BASE}/projects/${projectId}/learnings`, {
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch learnings");
  const data: CNSLearningPage = await res.json();
  return data.content || [];
}

export interface FetchProjectLearningsPageParams {
  page?: number;
  size?: number;
  sort?: string[];
  status?: LearningStatus;
}

export async function fetchProjectLearningsPage(
  projectId: string,
  params: FetchProjectLearningsPageParams = {}
): Promise<CNSLearningPage> {
  const query = new URLSearchParams();
  if (params.page != null) query.set("page", String(params.page));
  if (params.size != null) query.set("size", String(params.size));
  if (params.status) query.set("status", params.status);
  for (const s of params.sort ?? []) query.append("sort", s);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const res = await fetch(`${CNS_API_BASE}/projects/${projectId}/learnings${suffix}`, {
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch learnings");
  return res.json();
}

// Fetch learnings for an experiment
export async function fetchLearningsByExperiment(
  projectId: string,
  experimentId: string
): Promise<CNSLearning[]> {
  const res = await fetch(
    `${CNS_API_BASE}/projects/${projectId}/experiments/${experimentId}/learnings`,
    { credentials: "include" }
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch learnings for experiment");
  const data: CNSLearningPage = await res.json();
  return data.content || [];
}

// Fetch a single learning by ID
export async function fetchLearning(
  projectId: string,
  learningId: string
): Promise<CNSLearning> {
  const res = await fetch(
    `${CNS_API_BASE}/projects/${projectId}/learnings/${learningId}`,
    { credentials: "include" }
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch learning");
  return res.json();
}

/**
 * Applies JSON Patch to a learning. Supported paths include `/title`, `/dataReliability`,
 * `/status` (DRAFT | VALIDATED), and `/observations` (**replace** only, value = full
 * `Observation[]`). While **VALIDATED**, only `/status` **replace** with **`DRAFT`** is allowed;
 * other paths return **409**.
 */
export async function updateLearning(
  projectId: string,
  learningId: string,
  patches: JsonPatchOperation[]
): Promise<CNSLearning> {
  const res = await fetch(
    `${CNS_API_BASE}/projects/${projectId}/learnings/${learningId}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json-patch+json" },
      body: JSON.stringify(patches),
    }
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to update learning");
  return res.json();
}

// Delete a learning
export async function deleteLearning(
  projectId: string,
  learningId: string
): Promise<void> {
  const res = await fetch(
    `${CNS_API_BASE}/projects/${projectId}/learnings/${learningId}`,
    { method: "DELETE", credentials: "include" }
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to delete learning");
}
